// Copyright (c) 2024 by Audere

import { Controller, useFormContext } from "react-hook-form";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import { FlagOption, MultiSelectQuestion } from "../features/label/labelTypes";
import AdditionalInputField from "./AdditionalInputField";
import {
  isValidValue,
  validateQuestion,
  valueMatches,
} from "../utils/validateQuestion";
import i18n from "../../i18n";
import SmallChip from "./SmallChip";
import React, { ChangeEvent, useCallback, useEffect, useMemo } from "react";

interface MultiSelectQuestionComponentProps {
  name: string;
  question: MultiSelectQuestion;
  possibleResponses?: string[];
  displayFlags?: boolean;
  onChangeEvent: () => void;
}

const MultiSelectQuestionComponent: React.FC<
  MultiSelectQuestionComponentProps
> = ({ name, question, possibleResponses, displayFlags, onChangeEvent }) => {
  const { control, watch, setValue, unregister } = useFormContext();
  const response = `${name}.response`;
  const commentName = question.commentRequired
    ? `${name}.requiredComment`
    : `${name}.comment`;

  const watchedResponse = watch(response) as string[];
  const watchedComment = watch(commentName) as string;

  const showConditionalField = useMemo(
    () =>
      question.addComment &&
      (question.commentRequired === undefined ||
        valueMatches(watchedResponse, question.commentRequired)),
    [question, watchedResponse]
  );

  // Clear and remove conditional field
  useEffect(() => {
    if (validateQuestion(question, { response: watchedResponse })) {
      setValue(commentName, "");
      unregister(commentName);
    }
  }, [
    showConditionalField,
    setValue,
    unregister,
    commentName,
    question,
    watchedResponse,
  ]);

  const validateFn = useCallback(
    (value: string[] | undefined) => {
      const isValid = validateQuestion(question, {
        response: value,
        requiredComment: question.commentRequired ? watchedComment : undefined,
      });

      if (isValid) return true;
      if (!isValidValue(value)) return "At least one checked option required";
      if (showConditionalField && !isValidValue(watchedComment))
        return "Additional response required"; // Required conditional field invalid
    },
    [question, watchedComment, showConditionalField]
  );

  return (
    <section>
      <Controller
        name={response}
        control={control}
        defaultValue={[]}
        rules={{ validate: validateFn }}
        render={({ field }) => {
          const currentSelected = (
            Array.isArray(field.value) ? field.value : []
          ) as string[];
          const isOtherSelected = currentSelected.includes("other");

          const handleCheckboxChange =
            (optionId: string) => (e: ChangeEvent<HTMLInputElement>) => {
              if (optionId === "other") {
                field.onChange(e.target.checked ? [optionId] : []);
              } else {
                field.onChange(
                  e.target.checked
                    ? [...currentSelected, optionId]
                    : currentSelected.filter((val) => val !== optionId)
                );
              }
              onChangeEvent();
            };

          const errorMessage = validateFn(field.value as string[]);
          return (
            <>
              <FormGroup>
                {question.options.map((option) => {
                  const isChecked = currentSelected.includes(option.id);
                  return (
                    <FormControlLabel
                      key={option.id}
                      label={
                        displayFlags ? (
                          <>
                            <SmallChip flagData={option as FlagOption} />
                            {` - ${(option as FlagOption).definition}`}
                          </>
                        ) : (
                          option.name
                        )
                      }
                      control={
                        <Checkbox
                          checked={isChecked}
                          disabled={isOtherSelected && option.id !== "other"}
                          onChange={handleCheckboxChange(option.id)}
                        />
                      }
                    />
                  );
                })}
              </FormGroup>
              {errorMessage && (
                <Typography variant="caption" color="info">
                  {typeof errorMessage === "string" ? errorMessage : ""}
                </Typography>
              )}
            </>
          );
        }}
      />
      {showConditionalField && (
        <section style={{ marginTop: "1rem" }}>
          <Typography gutterBottom>
            {question.commentLabel ?? i18n.t("questions.defaultCommentLabel")}
          </Typography>
          <AdditionalInputField
            name={commentName}
            inputType={possibleResponses ? "dropdown" : "text"}
            possibleResponses={possibleResponses}
            onChangeEvent={onChangeEvent}
          />
        </section>
      )}
    </section>
  );
};

// Memoized to re-render only when props change
export default React.memo(MultiSelectQuestionComponent);
